import { useState, useEffect, useCallback } from 'react';
import {
  isPushSupported,
  registerServiceWorker,
  subscribeToPush,
  unsubscribeFromPush,
  isPushSubscribed,
  getPushPermissionState
} from '../utils/pushNotifications';

/**
 * React Hook for Push Notifications
 */
export const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Check support and current status on mount
  useEffect(() => {
    const checkStatus = async () => {
      setLoading(true);
      
      const supported = isPushSupported();
      setIsSupported(supported);
      
      if (supported) {
        // Register service worker
        await registerServiceWorker();
        
        // Check permission
        setPermission(getPushPermissionState());
        
        // Check subscription status
        const subscribed = await isPushSubscribed();
        setIsSubscribed(subscribed);
      }
      
      setLoading(false);
    };
    
    checkStatus();
  }, []);
  
  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications not supported on this device');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await subscribeToPush();
      setIsSubscribed(true);
      setPermission(getPushPermissionState());
      return true;
    } catch (err) {
      setError(err.message || 'Failed to enable push notifications');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);
  
  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      await unsubscribeFromPush();
      setIsSubscribed(false);
      return true;
    } catch (err) {
      setError(err.message || 'Failed to disable push notifications');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Toggle subscription
  const toggle = useCallback(async () => {
    if (isSubscribed) {
      return await unsubscribe();
    } else {
      return await subscribe();
    }
  }, [isSubscribed, subscribe, unsubscribe]);
  
  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    toggle
  };
};

export default usePushNotifications;
